import React, { useContext } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { colors } from '@/styles';

import { Calendar as GenericCalendar, CALENDAR_VIEWS } from '@onward-delivery/design/calendar';
import { TooltipProvider } from '@onward-delivery/design/tooltip';

import { DesignScope } from './blocks';
import { Legend, StatTile } from './calendarBlocks';
import { dayAggregate, dayBillingBuckets, dayCeilingRollup } from './dayMetrics';
import { Context } from './store';
import Day from './Day';

const CARD = css`
    box-shadow: 0px 1px 3px 0px #3f3f4426;
    box-shadow: 0px 0px 0px 1px #3f3f440d;
    border-radius: 5px;
    background: ${colors.white.primary};
`;

// Days closed arrives pre-formatted as "closed / billed"; everything else is a bare count.
const formatStat = (value) => (typeof value === 'number' ? value.toLocaleString() : value ?? 0);

// Built from the derivations rather than hand-written zero objects, so a new field can't be missing
// here. Hoisted out of render: Day is memoised and every empty cell shares this one object.
const EMPTY_DAY = {
    entries: [],
    aggregate: dayAggregate(),
    ceilingRollup: dayCeilingRollup(),
    percent: 0,
    measured: false,
    needsReview: false,
    billing: null,
    // A past cell with no work still draws the billing card's four quarters, so this cannot be
    // absent — it is read straight into a map.
    buckets: dayBillingBuckets(),
};

const STAT_TILES = [
    { key: 'resources', label: 'Resources', subtext: 'scheduled' },
    { key: 'stops', label: 'Stops', subtext: 'scheduled' },
    { key: 'pieces', label: 'Pieces', subtext: 'scheduled' },
    { key: 'avgCapacity', label: 'Avg capacity', subtext: 'fullest ceiling per day', suffix: '%', accent: true },
    { key: 'daysClosedRatio', label: 'Days closed', subtext: 'billed & paid' },
];

function Calendar() {
    const { callbacks, state, loading } = useContext(Context);
    const isWeek = state.calendarView === CALENDAR_VIEWS.WEEK;

    return (
        <Grid
            container
            direction="column"
            css={css`
                flex-wrap: nowrap;
                margin-bottom: 44px;
            `}
        >
            {/* KPIs are their own card, separated from the calendar rather than banded into it. */}
            <Grid
                container
                direction="row"
                css={css`
                    ${CARD}

                    flex: 0;
                    flex-basis: 0;
                    flex-wrap: nowrap;
                    margin-bottom: 16px;
                `}
            >
                {STAT_TILES.map(({ key, label, subtext, suffix, accent }, idx) => (
                    <StatTile
                        key={key}
                        label={label}
                        value={`${formatStat(state.stats?.[key])}${suffix || ''}`}
                        subtext={subtext}
                        accent={accent}
                        last={idx === STAT_TILES.length - 1}
                    />
                ))}
            </Grid>

            {/* Grid, weekday row, month nav and day flags are Calendar's; the legend and cell contents
                are ours. DesignScope also covers Day, since those cells are Calendar's children. */}
            <DesignScope>
                {/* Radix requires a provider above any Tooltip; Day's counts and bars are the only ones here. */}
                <TooltipProvider>
                    <GenericCalendar
                        month={state.month}
                        view={state.calendarView}
                        anchorDate={state.anchorDate}
                        showViewSelector
                        timeZone={state.tz}
                        today={state.todayKey}
                        days={state.days}
                        loading={loading.metrics}
                        header={<Legend />}
                        onMonthChange={callbacks.setMonth}
                        onAnchorChange={callbacks.setAnchorDate}
                        onViewChange={callbacks.setCalendarView}
                        onDayClick={({ dateKey }) => callbacks.viewDay(dateKey)}
                        renderDay={({ data, isToday, isPast, isCurrentMonth, dayOfMonth }) => (
                            <Day
                                dayData={data || EMPTY_DAY}
                                dayNumber={dayOfMonth}
                                isToday={isToday}
                                isPast={isPast}
                                isCurrentMonth={isCurrentMonth}
                                showResources={isWeek}
                            />
                        )}
                    />
                </TooltipProvider>
            </DesignScope>
        </Grid>
    );
}

export default Calendar;
